import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ConversationSummary, MessageThread, Message } from '@/types/message'

interface MessageState {
  conversations: ConversationSummary[]
  currentThread: MessageThread | null
  unreadCount: number
  isLoading: boolean
  error: string | null
  
  // UI state
  selectedConversationId: string | null
  isComposing: boolean
  searchQuery: string
  filters: {
    unreadOnly: boolean
    category: string | null
  }
}

const initialState: MessageState = {
  conversations: [],
  currentThread: null,
  unreadCount: 0,
  isLoading: false,
  error: null,
  selectedConversationId: null,
  isComposing: false,
  searchQuery: '',
  filters: {
    unreadOnly: false,
    category: null
  }
}

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    // Conversation management
    setConversations: (state, action: PayloadAction<ConversationSummary[]>) => {
      state.conversations = action.payload
      state.unreadCount = action.payload.reduce((total, conv) => total + conv.unreadCount, 0)
    },
    
    addConversation: (state, action: PayloadAction<ConversationSummary>) => {
      const existingIndex = state.conversations.findIndex(c => c.id === action.payload.id)
      if (existingIndex >= 0) {
        state.conversations[existingIndex] = action.payload
      } else {
        state.conversations.unshift(action.payload)
      }
      state.unreadCount = state.conversations.reduce((total, conv) => total + conv.unreadCount, 0)
    },
    
    updateConversation: (state, action: PayloadAction<{ id: string; updates: Partial<ConversationSummary> }>) => {
      const index = state.conversations.findIndex(c => c.id === action.payload.id)
      if (index >= 0) {
        state.conversations[index] = { ...state.conversations[index], ...action.payload.updates }
        state.unreadCount = state.conversations.reduce((total, conv) => total + conv.unreadCount, 0)
      }
    },
    
    // Thread management
    setCurrentThread: (state, action: PayloadAction<MessageThread | null>) => {
      state.currentThread = action.payload
      state.selectedConversationId = action.payload?.conversation.id || null
      
      // Mark conversation as read when opened
      if (action.payload) {
        const convIndex = state.conversations.findIndex(c => c.id === action.payload!.conversation.id)
        if (convIndex >= 0) {
          state.conversations[convIndex].unreadCount = 0
          if (state.conversations[convIndex].lastMessage) {
            state.conversations[convIndex].lastMessage.isRead = true
          }
          state.unreadCount = state.conversations.reduce((total, conv) => total + conv.unreadCount, 0)
        }
      }
    },
    
    addMessageToThread: (state, action: PayloadAction<{ message: Message; isFromCurrentUser: boolean }>) => {
      const { message, isFromCurrentUser } = action.payload
      if (state.currentThread && state.currentThread.conversation.id === message.conversationId) {
        state.currentThread.messages.push(message)
      }
      
      // Update conversation summary
      const convIndex = state.conversations.findIndex(c => c.id === message.conversationId)
      if (convIndex >= 0) {
        state.conversations[convIndex].lastMessage = {
          content: message.content,
          timestamp: message.createdAt,
          isFromCurrentUser,
          isRead: isFromCurrentUser
        }
        
        // Move conversation to top
        const conversation = state.conversations.splice(convIndex, 1)[0]
        state.conversations.unshift(conversation)
      }
    },
    
    markMessageAsRead: (state, action: PayloadAction<{ messageId: string; conversationId: string; userId: string }>) => {
      if (state.currentThread && state.currentThread.conversation.id === action.payload.conversationId) {
        const message = state.currentThread.messages.find(m => m.id === action.payload.messageId)
        if (message) {
          message.readBy = { ...(message.readBy || {}), [action.payload.userId]: new Date().toISOString() }
        }
      }
    },
    
    // UI state
    setSelectedConversation: (state, action: PayloadAction<string | null>) => {
      state.selectedConversationId = action.payload
    },
    
    setComposing: (state, action: PayloadAction<boolean>) => {
      state.isComposing = action.payload
    },
    
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    
    setFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    
    // Loading and error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    
    clearMessages: (state) => {
      state.conversations = []
      state.currentThread = null
      state.unreadCount = 0
      state.selectedConversationId = null
    }
  }
})

export const {
  setConversations,
  addConversation,
  updateConversation,
  setCurrentThread,
  addMessageToThread,
  markMessageAsRead,
  setSelectedConversation,
  setComposing,
  setSearchQuery,
  setFilters,
  setLoading,
  setError,
  clearMessages
} = messageSlice.actions

export default messageSlice.reducer
